import { FormikHelpers } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSubmit from 'src/core/forms/useSubmit';
import { dispatchDispatchOrderShipment } from 'src/core/api/apiRequests';
import { useCancelDispatchOrderByPickingList, useGetDispatchOrderByWarehouse, useGetDispatchOrderByPickingList, useGetShipmentLabelsByWarehouse, useUpdateShipmentDispatchOrder, useTriggerSyncShipmentDispatchOrder } from 'src/pages/warehouses/warehouseRequests';
import { DispatchPrintingSettings, PrintOutcome, printDispatchPdf, getDispatchResultData } from 'src/shared/printing/PrintingManager';

export const DispatchOrderLookup = ({ lookupId, warehouseId, onResolved }) => {
    const { rows: orderResponse, status: orderLookupStatus }: any = useGetDispatchOrderByWarehouse(lookupId, warehouseId);
    const { rows: pickingListResponse, status: pickingListLookupStatus }: any = useGetDispatchOrderByPickingList(lookupId, warehouseId);
    const hasResolved = useRef(false);

    useEffect(() => {
        if (hasResolved.current) return;

        const order = Array.isArray(orderResponse) ? orderResponse[0] : orderResponse;
        const pickingListOrder = Array.isArray(pickingListResponse) ? pickingListResponse[0] : pickingListResponse;
        // Let the page display a mismatch instead of silently discarding the order.
        const matchesOrder = requestSucceeded(orderLookupStatus) && order?.DispatchOrderId != null &&
            String(order.DispatchOrderId) === String(lookupId);
        const matchesPickingList = requestSucceeded(pickingListLookupStatus) && pickingListOrder?.DispatchOrderId != null;

        const dispatchOrder = matchesOrder ? order : matchesPickingList ? pickingListOrder : null;

        // An empty response from one request must not discard the other lookup.
        if (!dispatchOrder) return;

        hasResolved.current = true;
        onResolved(dispatchOrder);
    }, [lookupId, warehouseId, orderResponse, pickingListResponse, orderLookupStatus, pickingListLookupStatus, onResolved]);

    return null;
};

export const CancelDispatchOrderVerification = ({ pickingListId, warehouseId }) => {
    useCancelDispatchOrderByPickingList(pickingListId, warehouseId);
    return null;
};

export type ShipmentLabelJob = { key: number; rowId: string; shipmentId: number | string; packageId: number | string | null; packageCount: number; warehouseId: number | string; orderId: number | string; settings: DispatchPrintingSettings };

export type ShipmentLabelPrintResult = { outcome?: PrintOutcome; error?: string };

type ShipmentLabelResponse = { ShipmentId: number | string; ShipmentPackageId?: number | string | null; LabelPdf?: string | null; ReturnLabelPdf?: string | null };

const getShipmentAddressLabel = (response: ShipmentLabelResponse | ShipmentLabelResponse[], job: ShipmentLabelJob): string => {
    const labels = (Array.isArray(response) ? response : [response]).filter((label) => label && String(label.ShipmentId) === String(job.shipmentId));
    const matches = job.packageId != null ? labels.filter((label) => String(label.ShipmentPackageId) === String(job.packageId)) : job.packageCount === 1 ? labels : [];
    if (matches.length !== 1) throw new Error('Could not identify a unique address label for this package. Reload the order and try again.');
    if (typeof matches[0].LabelPdf !== 'string' || !matches[0].LabelPdf.trim()) throw new Error('No address label was returned for this package.');
    return matches[0].LabelPdf;
};

// Mounted outside the grid so scrolling or changing tabs cannot restart a request.
export const ShipmentLabelReprintJob = ({ job, onComplete }: { job: ShipmentLabelJob; onComplete: (result: ShipmentLabelPrintResult) => void }) => {
    const { rows: response, loading, status }: any = useGetShipmentLabelsByWarehouse(job.shipmentId, job.warehouseId);
    const claimed = useRef(false);
    const mounted = useRef(false);
    const completeRef = useRef(onComplete);
    completeRef.current = onComplete;
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            if (claimed.current || !mounted.current) return;
            claimed.current = true;
            completeRef.current({ error: 'Loading the address label timed out. Try reprinting it again.' });
        }, 30000);
        return () => window.clearTimeout(timeoutId);
    }, []);

    useEffect(() => {
        if (claimed.current || loading) return;
        const failed = status === 'error' || status === 'aborted' || status === 204 || (typeof status === 'number' && status >= 400);
        const data = response?.data ?? response;
        const hasResponse = Array.isArray(data) ? data.length > 0 : data?.ShipmentId != null;
        const succeeded = status === 'success' || (typeof status === 'number' && status >= 200 && status < 300);
        if (!hasResponse && !failed && !succeeded) return;
        claimed.current = true;
        if (failed || data == null) {
            completeRef.current({ error: `Could not load the address label for shipment ${job.shipmentId} (${status}).` });
            return;
        }
        let labelPdf: string;
        try { labelPdf = getShipmentAddressLabel(data, job); }
        catch (error) {
            completeRef.current({ error: error instanceof Error ? error.message : 'Could not identify the package address label.' });
            return;
        }
        const filename = `Shipment ${job.shipmentId}${job.packageId != null ? ` - Package ${job.packageId}` : ''} - Address label`;
        printDispatchPdf(labelPdf, job.settings, 'deliveryNote', filename)
            .then((outcome) => { if (mounted.current) completeRef.current({ outcome }); })
            .catch((error) => { if (mounted.current) completeRef.current({ error: error instanceof Error ? error.message : String(error) }); });
    }, [response, loading, status, job]);

    return null;
};

// UpdateShipment: save tracking first, then trigger customer sync without a body.
export type UpdateShipmentJob = { key: number; scope: string; warehouseId: number | string; rows: any[] };

export const UpdateShipment = ({ job, onComplete }: { job: UpdateShipmentJob; onComplete: (job: UpdateShipmentJob, rows: any[], error?: string) => void }) => {
    const shipmentId = job.rows[0].ShipmentId;
    const rows = useMemo(() => job.rows.filter((row) => String(row.ShipmentId) === String(shipmentId)), [job.rows, shipmentId]);
    const data = useMemo(() => ({ Comment: 'Added New Tracking via Webclient', ShipmentPackages: rows.map((row) => ({ Id: row.ShipmentPackages_Id, ShipmentId: row.ShipmentId, TrackingNumber: row.ShipmentPackages_TrackingNumber, ReturnLabelTrackingNumber: row.ShipmentPackages_ReturnLabelTrackingNumber })), }), [rows]);
    const [step, setStep] = useState<'update' | 'sync'>('update');
    const update = useUpdateShipmentDispatchOrder(shipmentId, job.warehouseId, data);
    const sync = useTriggerSyncShipmentDispatchOrder(step === 'sync' ? shipmentId : null, job.warehouseId);
    const completed = useRef(false);

    useEffect(() => {
        const response = step === 'update' ? update : sync;
        if (completed.current || response.loading) return;
        // rows can contain cached shipment data; only the request status confirms success.
        const status = response.status;
        const failed = status === 'error' || status === 'aborted' || Number(status) >= 400;
        const succeeded = status === 'success' || (Number(status) >= 200 && Number(status) < 300);
        if (!failed && !succeeded) return;
        if (!failed && step === 'update') { setStep('sync'); return; }
        completed.current = true;
        onComplete(job, rows, failed ? (step === 'update' ? `Could not save tracking for shipment ${shipmentId}. Your edits are retained; try again.` : `Tracking was saved for shipment ${shipmentId}, but customer sync failed. Click Sync Tracking to Customer to retry.`) : undefined);
    }, [step, update.status, update.loading, sync.status, sync.loading, job, rows, shipmentId, onComplete]);

    return null;
};

export type DispatchCompletionContext = { warehouseId: number | string; pickingListId: number; printDeliveryNote: boolean; formikHelpers: FormikHelpers<any> };

export type DispatchCompletionJob = DispatchCompletionContext & { firstResult: any; shipmentId: number | string; packages: any[] };

// Preserve package IDs, measurements and tracking from step one; omit label URLs.
export const toDispatchShipmentPackage = (item: any) => ({ Id: item.Id, ShipmentId: item.ShipmentId, Weight: item.Weight ?? null, Length: item.Length ?? null, Width: item.Width ?? null, Height: item.Height ?? null, Volume: item.Volume ?? null, DimensionUnitId: item.DimensionUnitId ?? null, WeightUnitId: item.WeightUnitId ?? null, VolumeUnitId: item.VolumeUnitId ?? null, TrackingNumber: item.TrackingNumber ?? null, ReturnLabelTrackingNumber: item.ReturnLabelTrackingNumber ?? null });

// A fresh hook instance for each attempt avoids treating an earlier result as completion.
export const CompleteDispatchShipment = ({ job, onComplete }: { job: DispatchCompletionJob; onComplete: (job: DispatchCompletionJob, data: any, error?: string) => void }) => {
    const buildRequest = useCallback(() => dispatchDispatchOrderShipment(job.packages, job.warehouseId, job.pickingListId, job.shipmentId, job.printDeliveryNote), [job]);
    const [result, submit] = useSubmit(buildRequest);
    const started = useRef(false);
    const completed = useRef(false);
    const completeRef = useRef(onComplete);
    completeRef.current = onComplete;
    useEffect(() => {
        if (started.current) return;
        started.current = true;
        Promise.resolve().then(() => submit(job.packages, job.formikHelpers)).catch((error) => {
            if (completed.current) return;
            completed.current = true;
            completeRef.current(job, null, `Could not complete shipment ${job.shipmentId}: ${error instanceof Error ? error.message : String(error)}. Retry completion; address labels will not be recreated.`);
        });
    }, [job, submit]);
    useEffect(() => {
        if (!result || completed.current) return;
        const data = getDispatchResultData(result);
        const response: any = result;
        const status = response.status ?? response.statusCode;
        const succeeded = data != null || status === 'success' || (Number(status) >= 200 && Number(status) < 300);
        completed.current = true;
        completeRef.current(job, data ?? response.data ?? null, succeeded ? undefined : `Could not confirm completion of shipment ${job.shipmentId}. Check the shipment and retry completion if needed; address labels will not be recreated.`);
    }, [result, job]);
    return null;
};

const requestSucceeded = (status: unknown) => status === 'success' || (Number(status) >= 200 && Number(status) < 300);
